import { useCallback } from 'react';
import Raven from 'raven-js';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { BaseRequestAdminModal } from './BaseRequestAdminModal';
import { RequestAdminInstallModal } from './RequestAdminInstallModal';
import { useAdmins, useAdminEmail } from './hooks/useAdmins';
import { useDetailsPageContext } from '../context/DetailsPageContext';
import { jsx as _jsx } from "react/jsx-runtime";
export const getRoleI18nKey = (role, portalScopes) => {
  let key = role.toLowerCase().replace(/ /g, '-');
  if (portalScopes) {
    if (portalScopes.includes('sales-nav-access')) {
      key += '-sales';
    } else if (portalScopes.includes('service-access')) {
      key += '-service';
    }
  }
  return `detailsPage.roles.${key}`;
};
export function RequestPermissionsModal({
  missingHubUserRoles = [],
  portalScopes,
  listingId,
  onClose,
  onOpenComplete,
  onSubmit
}) {
  const {
    trackInteraction
  } = useDetailsPageContext();
  const {
    admins: adminsData,
    adminsLoading,
    adminsError
  } = useAdmins();
  const {
    emailAdmin
  } = useAdminEmail();

  // Extract admins array from the query response
  const admins = Array.isArray(adminsData) ? adminsData : (adminsData === null || adminsData === void 0 ? void 0 : adminsData.admins) || [];
  const handleEmailAdmin = useCallback(async (adminUserId, listingIdParam, adminInstall, emailPermissionsList) => {
    try {
      await emailAdmin(adminUserId, listingIdParam || listingId, adminInstall, emailPermissionsList);

      // Track successful email send
      trackInteraction({
        action: 'admin email sent successfully'
      });

      // Close modal after successful send
      if (onSubmit) onSubmit();
      onClose();
    } catch (error) {
      // Error handling is managed by the useAdminEmail hook
      console.error('Failed to send admin email:', error);
    }
  }, [emailAdmin, listingId, trackInteraction, onSubmit, onClose]);
  const onlyMissingRoles = missingHubUserRoles.find(role => isValidI18nKey(getRoleI18nKey(role, portalScopes))) === undefined;
  const formatPermissions = useCallback(asString => {
    return missingHubUserRoles.map(role => {
      const roleKey = getRoleI18nKey(role, portalScopes);
      if (!isValidI18nKey(roleKey)) {
        // adding error-logging exception for elevated-partner-access
        // this is a known issue we will fix by overhauling
        // how this permissions modal works for unassignable roles
        if (role !== 'elevated-partner-access') {
          Raven.captureMessage(`Invalid role translation - ${role}`, {
            level: 'warning',
            extra: {
              role
            }
          });
        }
        return null;
      }
      return asString ? I18n.text(roleKey) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: roleKey
      }, role);
    });
  }, [missingHubUserRoles, portalScopes]);
  const emailPermissionsList = formatPermissions(true).filter(role => !!role);
  const modalPermissionsList = formatPermissions(false);
  if (emailPermissionsList.length === 0) {
    Raven.captureMessage('Empty list of role translations', {
      level: 'error',
      extra: {
        missingHubUserRoles
      }
    });
  }
  const commonProps = {
    listingId,
    onClose,
    onOpenComplete,
    onSubmit,
    admins,
    emailAdmin: handleEmailAdmin,
    loading: adminsLoading,
    error: adminsError ? /*#__PURE__*/_jsx(Alert, {
      type: "danger",
      marginBlockStart: "200",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.productDetailsCard.cta.error.ADMIN_FETCH.message"
      })
    }) : undefined
  };
  if (onlyMissingRoles) {
    return /*#__PURE__*/_jsx(RequestAdminInstallModal, Object.assign({}, commonProps));
  }
  return /*#__PURE__*/_jsx(BaseRequestAdminModal, Object.assign({}, commonProps, {
    adminInstall: false,
    emailPermissionsList: emailPermissionsList,
    help: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "detailsPage.productDetailsCard.cta.permissionsModal.help"
    }),
    modalHeader: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "detailsPage.productDetailsCard.cta.permissionsModal.header"
    }),
    modalPermissionsList: modalPermissionsList,
    subheader: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "detailsPage.productDetailsCard.cta.permissionsModal.subheader"
    })
  }));
}